import * as forms from '@/libraries/forms/forms'

const fields = [
  new forms.FormFieldGroup({
    name: 'Profile',
    fields: [
      new forms.FormField({
        type: forms.FormFieldType.TEXT,
        identifier: 'first_name',
        label: 'First Name',
        placeholder: 'John',
        inline: true,
        required: true,
      }),
      new forms.FormField({
        type: forms.FormFieldType.TEXT,
        identifier: 'last_name',
        label: 'Last Name',
        placeholder: 'Doe',
        inline: true,
        required: true,
      }),
      new forms.FormField({
        type: forms.FormFieldType.RADIO,
        label: 'Role',
        identifier: 'role',
        multiselect: false,
        options: [[
          new forms.FormFieldSelectOption('Resident', 'resident'),
          new forms.FormFieldSelectOption('Employee', 'employee'),
        ],
        [
          new forms.FormFieldSelectOption('Family', 'family'),
          new forms.FormFieldSelectOption('Outside Employee', 'outside_employee'),
          new forms.FormFieldSelectOption('Regular Visitor', 'regular_visitor'),
        ]],
        inline: true,
        required: true,
      }),
      new forms.FormField({
        type: forms.FormFieldType.RADIO,
        identifier: 'gender',
        label: 'Gender',
        multiselect: false,
        options: [
          new forms.FormFieldSelectOption('Male', 'male'),
          new forms.FormFieldSelectOption('Female', 'female'),
          new forms.FormFieldSelectOption('Other', 'other'),
        ],
        inline: true,
        required: true,
      }),
      new forms.FormField({
        type: forms.FormFieldType.RADIO,
        identifier: 'complexion',
        label: 'Race',
        multiselect: false,
        options: [[
          new forms.FormFieldSelectOption('Asian', 'asian'),
          new forms.FormFieldSelectOption('African American', 'black'),
          new forms.FormFieldSelectOption('Caucasian', 'caucasian'),
        ],
        [
          new forms.FormFieldSelectOption('Native American', 'native_american'),
          new forms.FormFieldSelectOption('Hispanic', 'hispanic'),
          new forms.FormFieldSelectOption('Other', 'Other'),
        ]],
        inline: true,
        required: true,
      }),
    ],
  }),
  new forms.FormFieldGroup({
    name: 'Resident Profile',
    layout: {
      flexProperties: {
        wrap: false,
      },
    },
    fields: [
      new forms.FormField({
        type: forms.FormFieldType.TEXT,
        identifier: 'room_number',
        label: 'Room Number',
        placeholder: '201',
        layout: {
          flexProperties: {
            grow: 2,
          },
        },
        required: false,
      }),
      new forms.FormField({
        type: forms.FormFieldType.TEXT,
        identifier: 'bed',
        label: 'Bed',
        placeholder: 'L',
        layout: {
          flexProperties: {
            grow: 2,
          },
        },
        required: false,
      }),
      new forms.FormField({
        type: forms.FormFieldType.RADIO,
        identifier: 'elopement_risk',
        label: 'Elopement Risk',
        multiselect: false,
        options: [
          new forms.FormFieldSelectOption('Yes', 'true'),
          new forms.FormFieldSelectOption('No', 'false'),
        ],
        layout: {
          direction: 'row',
        },
        required: true,
      }),
    ],
  }),
  new forms.FormFieldGroup({
    name: 'Employee Profile',
    fields: [
      new forms.FormField({
        type: forms.FormFieldType.TEXT,
        identifier: 'email',
        label: 'Email',
        placeholder: 'john@example.com',
        layout: {
          flexProperties: {
            grow: 2,
          },
        },
        required: false,
      }),
      new forms.FormField({
        type: forms.FormFieldType.RADIO,
        identifier: 'employee_role',
        label: 'Employee Role',
        multiselect: false,
        options: [
          new forms.FormFieldSelectOption('Administrator', 'administrator'),
          new forms.FormFieldSelectOption('Supervisor', 'supervisor'),
          new forms.FormFieldSelectOption('Nurse', 'nurse'),
        ],
        inline: true,
        required: false,
      }),
    ],
  }),
  new forms.FormFieldGroup({
    name: 'Photos',
    fields: [
      new forms.FormField({
        type: forms.FormFieldType.FILE,
        identifier: 'front',
        label: 'Front of Face',
        required: false,
        inline: true,
        fileTypes: 'image/*',
      }),
      new forms.FormField({
        type: forms.FormFieldType.FILE,
        identifier: 'side',
        label: 'Side of Face',
        required: false,
        inline: true,
        fileTypes: 'image/*',
      }),
    ],
  }),
]

/**
 * Removes the header prefix from base 64 data so Python handles it correctly on the server.
 *
 * @param {string} base64string A base64 encoded string with the data:// prefix.
 * @returns The base64 data without the prefix.
 */
function dataFromBase64 (base64string) {
  if (!base64string) {
    return null
  }

  const parts = base64string.split(',')

  if (!parts) {
    return null
  }

  return parts[parts.length - 1]
}

/**
 * Updates a submission with an image object.
 * Images with a `uuid` are treated as edits or deletes.
 * Images with no uuid and data are adds.
 *
 * @param {object} submission The submission to update.
 * @param {object} image The image to update.
 * @param {string} [orientation='front'] The orientation of the image.
 */
function updateImage (submission, image, orientation = 'front') {
  image = Object.assign({ orientation }, image)
  // see OwalFileInput:82
  const defaultImageURL = `https://s3.amazonaws.com/owal-clips/faces/thumbs/b97e21af-42ee-4433-8de9-199febf370ab.jpg`
  if (image.uuid && image.data_b64 === defaultImageURL) {
    submission.face_info.images.delete.push(image.uuid)
  } else if (image && image.data_b64 && image.data_b64.startsWith('http')) {
    console.log('Image is not edited. (It has an HTTP url.)')
  } else if (image && image.data_b64) {
    image.data_b64 = dataFromBase64(image.data_b64)
    if (image.uuid) {
      submission.face_info.images.edit.push(image)
    } else {
      submission.face_info.images.add.push(image)
    }
  }
}

const submissionRules = [
  new forms.FormTemplateRule('Format Face Submission', function (submission, index, parameters) {
    const faceProfile = {
      domain_uuid: submission.metadata.domain_uuid,
      face_info: {
        first_name: submission.fields.first_name,
        last_name: submission.fields.last_name,
        role: submission.fields.role,
        room: submission.fields.room_number,
        bed: submission.fields.bed,
        complexion: submission.fields.complexion,
        gender: submission.fields.gender,
        email: submission.fields.email,
        employee_role: submission.fields.employee_role,
        elopement_risk: submission.fields.elopement_risk === 'true',
        images: {
          add: [],
          edit: [],
          delete: [],
        },
      },
    }
    if (submission.fields.front) {
      updateImage(faceProfile, submission.fields.front)
    }

    if (submission.fields.side) {
      updateImage(faceProfile, submission.fields.side, 'side')
    }
    return faceProfile
  }),
]

const initializationRules = [
  new forms.FormTemplateRule('Convert Face Into Submission', function (face, index, parameters) {
    const initialValues = {
      domain_uuid: face.domain_uuid,
      first_name: face.first_name,
      last_name: face.last_name,
      role: face.role,
      room_number: face.room,
      bed: face.bed,
      complexion: face.complexion,
      gender: face.gender,
      email: face.email,
      employee_role: face.employee_role,
      elopement_risk: face.elopement_risk ? 'true' : 'false',
      images: {
        add: [],
        edit: [],
        delete: [],
      },
    }
    if (face.images.length) {
      const front = face.images[0]
      const side = face.images[1]

      if (front) {
        initialValues.front = {
          data_b64: front.thumbnail_uri,
          uuid: front.uuid,
        }
      }
      if (side) {
        initialValues.side = {
          data_b64: side.thumbnail_uri,
          uuid: side.uuid,
        }
      }
    }

    // Populate face from person_name
    if (face.person_name && !face.first_name) {
      const parts = face.person_name.split(' ')

      if (parts.length > 2) {
        parts.first_name = `${parts[0]} ${parts[1]}`
        parts.last_name = parts[2]
      } else if (parts.length === 2) {
        initialValues.first_name = parts[0]
        initialValues.last_name = parts[1]
      } else {
        initialValues.first_name = face.person_name
      }
    }

    const legacyEmployee = !!face.employee
    const legacyVisitor = !!face.visitor
    const legacyResident = !!face.resident

    if (legacyEmployee) {
      initialValues.role = 'employee'
    } else if (legacyVisitor) {
      initialValues.role = 'visitor'
    } else if (legacyResident) {
      initialValues.role = 'resident'
    }

    const submission = new forms.FormSubmission('io.owal.addface', fields, initialValues)
    return submission
  }),
]
const submissionTemplate = new forms.FormTemplate('Upload Face Resident', submissionRules)
const initializationTemplate = new forms.FormTemplate('Upload Face Resident', initializationRules)

export const addFace = new forms.Form('Add Face', fields, submissionTemplate, initializationTemplate)
